(() => {
  const PARAM_PREFIX = 'cf_';
  const DATA_SELECTOR = 'script.js-child-metafields[data-parent-id]';
  const FILTERS_SELECTOR = '[data-child-metafield-filters]';
  const PRODUCT_CARD_SELECTOR = '.grid-product[data-product-id]';
  const COUNT_SELECTOR = '.collection-filter__item--count';
  const PAGINATION_SELECTOR = '.pagination';
  const GRID_SELECTOR = '.collection-grid__wrapper .grid.grid--uniform';
  const SORT_SELECTOR = '#SortBy';
  const PER_PAGE_STORAGE_KEY = () => `cfPerPage:${window.location.pathname}`;

  function rememberOriginalHref(a) {
    if (!(a instanceof HTMLAnchorElement)) return;
    if (!a.dataset.cfOriginalHref) {
      a.dataset.cfOriginalHref = a.getAttribute('href') || a.href || '';
    }
  }

  function rememberOriginalPaginationHtml(root = document) {
    const pagEl = root.querySelector(PAGINATION_SELECTOR);
    if (!pagEl) return;
    if (!pagEl.dataset.cfOriginalHtml) {
      pagEl.dataset.cfOriginalHtml = pagEl.innerHTML;
    }
  }

  function restoreOriginalPagination(root = document) {
    const pagEl = root.querySelector(PAGINATION_SELECTOR);
    if (!pagEl) return;

    if (pagEl.dataset.cfOriginalHtml != null) {
      pagEl.innerHTML = pagEl.dataset.cfOriginalHtml;
      pagEl.style.display = '';
      return;
    }

    pagEl.querySelectorAll('[data-cf-hidden]').forEach((el) => {
      el.style.display = '';
      el.removeAttribute('data-cf-hidden');
    });

    pagEl.querySelectorAll('a[data-cf-original-href]').forEach((a) => {
      const original = a.dataset.cfOriginalHref;
      if (original) a.setAttribute('href', original);
      delete a.dataset.cfOriginalHref;
    });
  }

  function rememberOriginalCount(root = document) {
    const countEl = root.querySelector(COUNT_SELECTOR);
    if (!countEl) return;
    if (!countEl.dataset.cfOriginalHtml) {
      countEl.dataset.cfOriginalHtml = countEl.innerHTML;
    }
  }

  function restoreOriginalCount(root = document) {
    const countEl = root.querySelector(COUNT_SELECTOR);
    if (!countEl) return;
    if (countEl.dataset.cfOriginalHtml != null) {
      countEl.innerHTML = countEl.dataset.cfOriginalHtml;
    }
  }

  function rememberOriginalGrid(root = document) {
    const gridEl = root.querySelector(GRID_SELECTOR);
    if (!gridEl) return;
    if (!gridEl.dataset.cfOriginalHtml) {
      gridEl.dataset.cfOriginalHtml = gridEl.innerHTML;
    }
  }

  function restoreOriginalGrid(root = document) {
    const gridEl = root.querySelector(GRID_SELECTOR);
    if (!gridEl) return;
    if (gridEl.dataset.cfOriginalHtml != null) {
      gridEl.innerHTML = gridEl.dataset.cfOriginalHtml;
    }
  }

  /** @param {URLSearchParams} params */
  function extractCfParams(params) {
    /** @type {Array<[string,string]>} */
    const out = [];
    for (const [k, v] of params.entries()) {
      if (!k.startsWith(PARAM_PREFIX)) continue;
      if (!v) continue;
      out.push([k, v]);
    }
    return out;
  }

  function stripCfParams(params) {
    const out = new URLSearchParams(params.toString());
    for (const k of Array.from(out.keys())) {
      if (k.startsWith(PARAM_PREFIX)) out.delete(k);
    }
    return out;
  }

  function getSortValue(root = document) {
    const el = root.querySelector(SORT_SELECTOR) || document.querySelector(SORT_SELECTOR);
    if (el && el.value) return el.value;
    return new URLSearchParams(window.location.search).get('sort_by') || '';
  }

  function isBestSellingCustom(root = document) {
    return getSortValue(root) === 'best-selling';
  }

  /** @returns {Record<string, Set<string>>} */
  function readActiveFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    /** @type {Record<string, Set<string>>} */
    const active = {};

    for (const [k, v] of params.entries()) {
      if (!k.startsWith(PARAM_PREFIX)) continue;
      const key = k.slice(PARAM_PREFIX.length);
      const vals = (v || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      if (!vals.length) continue;
      active[key] = new Set(vals);
    }
    return active;
  }

  function writeActiveFiltersToUrl(active) {
    const params = new URLSearchParams(window.location.search);

    // remove existing cf_* keys
    for (const k of Array.from(params.keys())) {
      if (k.startsWith(PARAM_PREFIX)) params.delete(k);
    }

    for (const [key, set] of Object.entries(active)) {
      const vals = Array.from(set);
      if (!vals.length) continue;
      params.set(`${PARAM_PREFIX}${key}`, vals.join(','));
    }

    const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash || ''}`;
    window.history.replaceState({}, '', newUrl);
  }

  function normalizeKeyFromName(name) {
    if (!name || !name.startsWith(PARAM_PREFIX)) return null;
    return name.slice(PARAM_PREFIX.length);
  }

  /** @returns {Map<string, any>} parentId -> data */
  function parseProductData(root = document) {
    const map = new Map();
    root.querySelectorAll(DATA_SELECTOR).forEach((el) => {
      try {
        const json = JSON.parse(el.textContent || '{}');
        const parentId = String(json.parentId ?? el.getAttribute('data-parent-id') ?? '');
        if (parentId) map.set(parentId, json);
      } catch (e) {
        // ignore invalid
      }
    });
    return map;
  }

  /**
   * A parent matches if ANY single child matches ALL active filter groups.
   * Within a filter group, match is OR across selected values.
   */
  function doesChildMatch(child, active) {
    const mf = (child && child.metafields) || {};
    for (const [key, selected] of Object.entries(active)) {
      const childVals = Array.isArray(mf[key]) ? mf[key] : [];
      let ok = false;
      for (const v of childVals) {
        if (selected.has(v)) { ok = true; break; }
      }
      if (!ok) return false;
    }
    return true;
  }

  function doesParentMatch(data, active) {
    const children = (data && Array.isArray(data.children) && data.children.length) ? data.children : [];
    for (const child of children) {
      if (doesChildMatch(child, active)) return true;
    }
    return false;
  }

  function safeThemeReinit() {
    try {
      if (window.theme && typeof window.theme.reinitProductGridItem === 'function') {
        window.theme.reinitProductGridItem();
      }
    } catch (e) {
      // ignore
    }
  }

  function applyFiltering({ root = document } = {}) {
    const active = readActiveFiltersFromUrl();
    const hasAny = Object.keys(active).length > 0;
    const dataByParent = parseProductData(root);

    root.querySelectorAll(PRODUCT_CARD_SELECTOR).forEach((card) => {
      const parentId = String(card.getAttribute('data-product-id') || '');
      const data = dataByParent.get(parentId);
      const children = (data && Array.isArray(data.children) && data.children.length) ? data.children : [];

      let matchingChildHandles = null;
      let matchesParent = true;

      if (hasAny) {
        matchesParent = false;
        matchingChildHandles = new Set();

        for (const child of children) {
          if (doesChildMatch(child, active)) {
            matchesParent = true;
            if (child && child.handle) matchingChildHandles.add(String(child.handle));
          }
        }
      }

      card.style.display = matchesParent ? '' : 'none';

      // If grouped table exists, hide non-matching child rows (only when filters active)
      const group = card.querySelector('.group-div');
      if (group) {
        group.querySelectorAll('.group-item[data-child-handle]').forEach((row) => {
          if (!hasAny) {
            row.style.display = '';
            return;
          }
          const h = String(row.getAttribute('data-child-handle') || '');
          row.style.display = matchingChildHandles && matchingChildHandles.has(h) ? '' : 'none';
        });
      }
    });
  }

  function getTotalPages(root = document) {
    const pag = root.querySelector(PAGINATION_SELECTOR);
    if (!pag) return 1;

    let max = 1;
    pag.querySelectorAll('a, span').forEach((el) => {
      const t = (el.textContent || '').trim();
      const n = parseInt(t, 10);
      if (!Number.isNaN(n)) max = Math.max(max, n);
      const href = el.getAttribute && el.getAttribute('href');
      if (href && href.includes('page=')) {
        try {
          const u = new URL(href, window.location.origin);
          const pn = parseInt(u.searchParams.get('page') || '', 10);
          if (!Number.isNaN(pn)) max = Math.max(max, pn);
        } catch (e) {
          // ignore
        }
      }
    });

    return max || 1;
  }

  function getCurrentPage() {
    const params = new URLSearchParams(window.location.search);
    const p = parseInt(params.get('page') || '1', 10);
    return Number.isNaN(p) || p < 1 ? 1 : p;
  }

  function buildPageUrl(pageNumber) {
    const params = stripCfParams(new URLSearchParams(window.location.search));
    params.set('page', String(pageNumber));

    // re-add cf_* params
    const cf = extractCfParams(new URLSearchParams(window.location.search));
    cf.forEach(([k, v]) => params.set(k, v));

    return `${window.location.pathname}?${params.toString()}${window.location.hash || ''}`;
  }

  function buildServerPageUrl(pageNumber) {
    const params = stripCfParams(new URLSearchParams(window.location.search));
    params.set('page', String(pageNumber));
    return `${window.location.pathname}?${params.toString()}${window.location.hash || ''}`;
  }

  function sessionCacheGet(key) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function sessionCacheSet(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      // ignore
    }
  }

  function getPerPage(root = document) {
    // URL param takes priority (server-side per_page selection)
    const urlPerPage = parseInt(new URLSearchParams(window.location.search).get('per_page') || '', 10);
    if (urlPerPage === 20 || urlPerPage === 40 || urlPerPage === 60) {
      return urlPerPage;
    }

    let perPage = 0;
    try {
      perPage = parseInt(sessionStorage.getItem(PER_PAGE_STORAGE_KEY()) || '0', 10) || 0;
    } catch (e) {
      perPage = 0;
    }

    const gridEl = root.querySelector(GRID_SELECTOR);
    if (gridEl) {
      const observed = gridEl.querySelectorAll(PRODUCT_CARD_SELECTOR).length;
      if (observed > perPage) perPage = observed;
    }

    if (!perPage) perPage = 20; // default matches server default

    try {
      sessionStorage.setItem(PER_PAGE_STORAGE_KEY(), String(perPage));
    } catch (e) {
      // ignore
    }

    return perPage;
  }

  function handlePerPageClick(evt) {
    const btn = evt.target && evt.target.closest && evt.target.closest('[data-per-page]');
    if (!btn) return;
    const perPage = btn.getAttribute('data-per-page');
    if (!perPage) return;

    const params = new URLSearchParams(window.location.search);
    params.set('per_page', perPage);
    params.delete('page');
    window.location.href = `${window.location.pathname}?${params.toString()}${window.location.hash || ''}`;
  }

  async function fetchPageMatches(pageNumber, active) {
    const url = buildServerPageUrl(pageNumber);
    const res = await fetch(url, { credentials: 'same-origin' });
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const gridEl = doc.querySelector(GRID_SELECTOR);
    const dataByParent = parseProductData(doc);
    /** @type {Array<string>} */
    const matchesHtml = [];

    if (!gridEl) return matchesHtml;

    gridEl.querySelectorAll(PRODUCT_CARD_SELECTOR).forEach((card) => {
      const parentId = String(card.getAttribute('data-product-id') || '');
      const data = dataByParent.get(parentId);
      if (data && doesParentMatch(data, active)) {
        matchesHtml.push(card.outerHTML);
      }
    });

    return matchesHtml;
  }

  async function fetchPageCards(pageNumber) {
    const url = buildServerPageUrl(pageNumber);
    const res = await fetch(url, { credentials: 'same-origin' });
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const gridEl = doc.querySelector(GRID_SELECTOR);
    if (!gridEl) return [];
    return Array.from(gridEl.querySelectorAll(PRODUCT_CARD_SELECTOR)).map((card) => card.outerHTML);
  }

  function salesCountFromHtml(html) {
    const m = html.match(/data-sales-count=\"([0-9]+)/);
    if (!m) return 0;
    const n = parseInt(m[1], 10);
    return Number.isNaN(n) ? 0 : n;
  }

  // Inject loading overlay CSS once
  (function injectCfLoadingStyles() {
    if (document.getElementById('cf-loading-styles')) return;
    const style = document.createElement('style');
    style.id = 'cf-loading-styles';
    style.textContent = `
      .cf-loading { position: relative; opacity: 0.5; pointer-events: none; transition: opacity 0.2s; min-height: 200px; }
      .cf-loading::after {
        content: '';
        position: absolute;
        top: 50%; left: 50%;
        width: 36px; height: 36px;
        margin: -18px 0 0 -18px;
        border: 3px solid #ccc;
        border-top-color: #333;
        border-radius: 50%;
        animation: cf-spin 0.7s linear infinite;
        z-index: 10;
      }
      @keyframes cf-spin { to { transform: rotate(360deg); } }
    `;
    document.head.appendChild(style);
  })();

  let paginationComputeToken = 0;
  async function updateTotalCountAndPagination(root = document) {
    rememberOriginalPaginationHtml(root);
    rememberOriginalCount(root);
    rememberOriginalGrid(root);
    const active = readActiveFiltersFromUrl();
    const hasAny = Object.keys(active).length > 0;
    const isBestSelling = isBestSellingCustom(root);
    const customMode = hasAny || isBestSelling;
    const pagEl = root.querySelector(PAGINATION_SELECTOR);
    const countEl = root.querySelector(COUNT_SELECTOR);
    const gridEl = root.querySelector(GRID_SELECTOR);

    if (!customMode) {
      restoreOriginalPagination(root);
      restoreOriginalCount(root);
      restoreOriginalGrid(root);
      applyFiltering({ root });
      safeThemeReinit();
      return;
    }

    const totalPages = getTotalPages(root);
    const perPage = getPerPage(root);

    // Safety guard: if the collection has too many pages to feasibly fetch
    // client-side, fall back to server-side pagination to avoid infinite loading
    const MAX_FETCHABLE_PAGES = 50;
    if (totalPages > MAX_FETCHABLE_PAGES) {
      restoreOriginalPagination(root);
      restoreOriginalCount(root);
      restoreOriginalGrid(root);
      applyFiltering({ root });
      safeThemeReinit();
      return;
    }

    const token = ++paginationComputeToken;
    const nonCfParams = stripCfParams(new URLSearchParams(window.location.search)).toString();
    const cfParams = new URLSearchParams(window.location.search).toString();
    const cacheKey = hasAny
      ? `cfMatches:${window.location.pathname}?${nonCfParams}||${cfParams}`
      : `cfAllCards:${window.location.pathname}?${nonCfParams}`;

    const cached = sessionCacheGet(cacheKey);
    /** @type {Array<string> | null} */
    let allMatches = (cached && cached.allMatches) || null;

    if (!allMatches) {
      allMatches = [];
      if (gridEl) gridEl.classList.add('cf-loading');

      // Concurrency-limited fetches, preserve ordering via per-page buckets
      const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
      const limit = 4;
      let idx = 0;
      /** @type {Record<string, Array<string>>} */
      const perPageMatches = {};

      async function worker() {
        while (idx < pages.length) {
          const p = pages[idx++];
          const matches = hasAny ? await fetchPageMatches(p, active) : await fetchPageCards(p);
          perPageMatches[String(p)] = matches;
          if (paginationComputeToken !== token) return; // cancelled
        }
      }

      const workers = Array.from({ length: Math.min(limit, pages.length) }, () => worker());
      await Promise.all(workers);

      if (paginationComputeToken !== token) {
        if (gridEl) gridEl.classList.remove('cf-loading');
        return;
      }

      pages.forEach((p) => {
        const arr = perPageMatches[String(p)] || [];
        allMatches = allMatches.concat(arr);
      });

      sessionCacheSet(cacheKey, { allMatches });
      if (gridEl) gridEl.classList.remove('cf-loading');
    }

    if (isBestSelling) {
      const decorated = allMatches.map((html, idx) => ({ html, idx, sc: salesCountFromHtml(html) }));
      decorated.sort((a, b) => (b.sc - a.sc) || (a.idx - b.idx));
      allMatches = decorated.map((x) => x.html);
    }

    const total = allMatches.length;
    if (countEl) countEl.textContent = `${total} ${total === 1 ? 'Result' : 'Results'}`;

    const virtualTotalPages = Math.max(1, Math.ceil(total / perPage));
    let currentPage = getCurrentPage();
    if (currentPage > virtualTotalPages) currentPage = 1;

    // Render the current virtual page slice (packed, so pages are full)
    if (gridEl) {
      const start = (currentPage - 1) * perPage;
      const end = start + perPage;
      gridEl.innerHTML = allMatches.slice(start, end).join('');
    }

    // Apply child-row filtering within the rendered slice
    applyFiltering({ root });
    safeThemeReinit();

    // Render a virtual pagination UI based on filtered total
    if (pagEl) {
      if (virtualTotalPages <= 1) {
        pagEl.style.display = 'none';
      } else {
        pagEl.style.display = '';
        const parts = [];

        if (currentPage > 1) {
          parts.push(`<span class="prev"><a href="${buildPageUrl(currentPage - 1)}" title="Previous"><svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="element-icon element-icon-chevron-left element-icon--line" viewBox="0 0 284.49 498.98" fill="currentColor" width="20" height="20"><path d="M249.49 0a35 35 0 0 1 24.75 59.75L84.49 249.49l189.75 189.74a35.002 35.002 0 1 1-49.5 49.5L10.25 274.24a35 35 0 0 1 0-49.5L224.74 10.25A34.89 34.89 0 0 1 249.49 0"/></svg><span class="icon__fallback-text">Previous</span></a></span>`);
        }

        for (let p = 1; p <= virtualTotalPages; p++) {
          if (p === currentPage) {
            parts.push(`<span class="page current">${p}</span>`);
          } else {
            parts.push(`<span class="page"><a href="${buildPageUrl(p)}">${p}</a></span>`);
          }
        }

        if (currentPage < virtualTotalPages) {
          parts.push(`<span class="next"><a href="${buildPageUrl(currentPage + 1)}" title="Next"><svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="element-icon element-icon-chevron-right element-icon--line" viewBox="0 0 284.49 498.98" fill="currentColor" width="20" height="20"><path d="M35 498.98a35 35 0 0 1-24.75-59.75l189.74-189.74L10.25 59.75a35.002 35.002 0 0 1 49.5-49.5l214.49 214.49a35 35 0 0 1 0 49.5L59.75 488.73A34.89 34.89 0 0 1 35 498.98"/></svg><span class="icon__fallback-text">Next</span></a></span>`);
        }

        pagEl.innerHTML = parts.join('');
      }
    }
  }

  function syncUiFromUrl(root = document) {
    const active = readActiveFiltersFromUrl();

    root.querySelectorAll(`${FILTERS_SELECTOR} input[type="checkbox"].child-metafield-filters__input`).forEach((cb) => {
      const key = normalizeKeyFromName(cb.name);
      if (!key) return;
      const set = active[key];
      cb.checked = !!(set && set.has(cb.value));
    });
  }

  function updateFilterBadge(root = document) {
    const badges = root.querySelectorAll('[data-cf-badge]');
    if (!badges.length) return;

    const active = readActiveFiltersFromUrl();
    let cfCount = 0;
    for (const set of Object.values(active)) {
      cfCount += set.size;
    }

    badges.forEach((badge) => {
      const nativeCount = parseInt(badge.getAttribute('data-native-filter-count') || '0', 10);
      const total = nativeCount + cfCount;
      badge.textContent = total > 0 ? `(${total})` : '';

      const btn = badge.closest('.js-drawer-open-collection-filters');
      if (btn) {
        btn.classList.toggle('btn--tertiary-active', total > 0);
      }
    });
  }

  function handleCheckboxChange(evt) {
    const target = evt.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.type !== 'checkbox') return;
    if (!target.classList.contains('child-metafield-filters__input')) return;

    const key = normalizeKeyFromName(target.name);
    if (!key) return;

    const active = readActiveFiltersFromUrl();
    if (!active[key]) active[key] = new Set();

    if (target.checked) active[key].add(target.value);
    else active[key].delete(target.value);

    if (active[key].size === 0) delete active[key];

    writeActiveFiltersToUrl(active);

    // sync both sidebar + drawer UI, then filter products
    syncUiFromUrl(document);
    updateFilterBadge(document);
    // Reset to page 1 when filters change (prevents landing on an empty virtual page)
    try {
      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}${window.location.hash || ''}`);
    } catch (e) {
      // ignore
    }
    updateTotalCountAndPagination(document);
  }

  function handleClearClick(evt) {
    const btn = evt.target && evt.target.closest && evt.target.closest('[data-cf-clear]');
    if (!btn) return;
    evt.preventDefault();
    writeActiveFiltersToUrl({});
    syncUiFromUrl(document);
    updateFilterBadge(document);
    updateTotalCountAndPagination(document);
  }

  function handleSortChangeCapture(evt) {
    const el = evt.target;
    if (!(el instanceof HTMLSelectElement)) return;
    if (el.id !== 'SortBy') return;

    const perPage = new URLSearchParams(window.location.search).get('per_page');

    if (el.value === 'best-selling') {
      evt.preventDefault();
      evt.stopImmediatePropagation();

      try {
        const params = new URLSearchParams(window.location.search);
        params.set('sort_by', 'best-selling');
        params.delete('page');
        window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}${window.location.hash || ''}`);
      } catch (e) {
        // ignore
      }

      updateTotalCountAndPagination(document);
      return;
    }

    // For all other sort values: intercept to preserve per_page
    if (perPage) {
      evt.preventDefault();
      evt.stopImmediatePropagation();

      try {
        const params = new URLSearchParams(window.location.search);
        params.set('sort_by', el.value);
        params.delete('page');
        window.location.href = `${window.location.pathname}?${params.toString()}${window.location.hash || ''}`;
      } catch (e) {
        // ignore
      }
    }
    // else: let theme handle sort normally
  }

  // Preserve cf_* params and per_page when navigating via pagination links
  function handlePaginationClick(evt) {
    const link = evt.target && evt.target.closest && evt.target.closest('.pagination a');
    if (!link) return;

    const currentCf = extractCfParams(new URLSearchParams(window.location.search));
    const perPage = new URLSearchParams(window.location.search).get('per_page');

    if (!currentCf.length && !perPage) return;

    try {
      const url = new URL(link.href, window.location.origin);
      currentCf.forEach(([k, v]) => url.searchParams.set(k, v));
      if (perPage) url.searchParams.set('per_page', perPage);
      link.href = url.toString();
    } catch (e) {
      // ignore
    }
  }

  // Preserve per_page when navigating via filter/tag links
  function handleFilterLinkClick(evt) {
    const perPage = new URLSearchParams(window.location.search).get('per_page');
    if (!perPage) return;

    const link = evt.target && evt.target.closest && evt.target.closest('a[href]');
    if (!link) return;
    if (link.closest('.pagination')) return; // handled by handlePaginationClick

    try {
      const url = new URL(link.href, window.location.origin);
      if (url.pathname !== window.location.pathname) return; // different page
      if (!url.search) return; // no query params, likely a UI anchor
      if (url.searchParams.has('per_page')) return; // already set

      url.searchParams.set('per_page', perPage);
      link.href = url.toString();
    } catch (e) {
      // ignore
    }
  }

  function bind(root = document) {
    // Avoid double-binding by using a marker on documentElement
    if (!document.documentElement.dataset.cfBound) {
      document.addEventListener('change', handleCheckboxChange);
      document.addEventListener('click', handleClearClick);
      document.addEventListener('change', handleSortChangeCapture, true);
      document.addEventListener('click', handlePaginationClick, true);
      document.addEventListener('click', handleFilterLinkClick, true);
      document.addEventListener('click', handlePerPageClick);
      document.documentElement.dataset.cfBound = 'true';
    }

    syncUiFromUrl(root);
    updateFilterBadge(root);
    updateTotalCountAndPagination(root);
  }

  // Initial loads
  document.addEventListener('DOMContentLoaded', () => bind(document));
  document.addEventListener('page:loaded', () => bind(document));
  document.addEventListener('collection:reloaded', () => bind(document));
})();

